import { performGetRequest } from '@atlassian/jira-fetch/src/utils/requests.tsx';
import {
	createResource,
	useResource,
	type ResourceStoreContext,
	type RouterDataContext,
	type RouteResource,
	type UseResourceHookResponse,
} from '@atlassian/react-resource-router';
// eslint-disable-next-line jira/import-whitelist
import { fireErrorAnalytics } from '@jira/platform__error-reporting/src/fireErrorAnalytics.tsx';
// eslint-disable-next-line jira/import-whitelist
import { memoizedFetchSiteUserPersonalization } from '@jira/platform__services-personalization/src/memoizedFetchSiteUserPersonalization.tsx';
import type {
	PersonalizationApiTraitData,
	PersonalizationApiTraitDataResponse,
} from './FetchSiteUserTraitsPersonalizationApiTraitData.types.tsx';
import { ANALYTICS_ID, RESOURCE_KEY } from './FetchSiteUserTraitsResourceType.constants.tsx';
import { PACKAGE_NAME } from './packageName.constants.tsx';

const getData = async (
	_: RouterDataContext,
	{ tenantContext }: ResourceStoreContext,
): Promise<PersonalizationApiTraitData[] | null> => {
	try {
		if (tenantContext.isAnonymous) {
			return null;
		}
		const accountId = tenantContext.atlassianAccountId ?? '';
		const cloudId = tenantContext.cloudId;

		const ssrUrl = `/api/v3/personalization/site/${cloudId}/user/${accountId}`;

		const response = __SERVER__
			? (
					await performGetRequest<PersonalizationApiTraitDataResponse>(ssrUrl, {
						headers: {
							'x-ssr-service-name': 'tap-delivery',
						},
					})
				)?.attributes
			: await memoizedFetchSiteUserPersonalization({ cloudId, userId: accountId });

		if (response) {
			return response;
		}

		return null;
		// oxlint-disable-next-line @typescript-eslint/no-explicit-any
	} catch (error: any) {
		fireErrorAnalytics({
			error,
			meta: {
				id: ANALYTICS_ID,
				packageName: PACKAGE_NAME,
			},
			sendToPrivacyUnsafeSplunk: true,
		});
	}
	return null;
};

export const fetchSiteUserTraitsResource: RouteResource<PersonalizationApiTraitData[] | null> =
	createResource<PersonalizationApiTraitData[] | null>({
		type: 'RESOURCE_TYPE_RECOMMENDATIONS_FETCH_USER_TRAITS',
		getKey: () => RESOURCE_KEY,
		getData,
		maxAge: Number.MAX_VALUE,
		isCritical: true,
	});

export function useFetchSiteUserTraitsResource(): UseResourceHookResponse<
	PersonalizationApiTraitData[] | null
> {
	return useResource(fetchSiteUserTraitsResource);
}
